import { ref, readonly, onMounted, onUnmounted } from 'vue';

// Shared reactive state
const refreshing = ref(false);
const registration = ref(null);
const updateExists = ref(false);

/**
 * Browser detection utilities
 */
const isIOS = () => {
    return (/ipad|iphone|ipod/.test(navigator.userAgent.toLowerCase()));
};

const isInStandaloneMode = () => {
    // Check iOS Safari
    if (('standalone' in window.navigator) && (window.navigator.standalone)) {
        return true;
    }
    // Check other browsers (Chrome, Edge, Firefox, etc.)
    if (window.matchMedia('(display-mode: standalone)').matches) {
        return true;
    }
    // Check Android WebView
    if (document.referrer.includes('android-app://')) {
        return true;
    }
    return false;
};

const isChrome = () => {
    return (window.navigator.userAgent.toLowerCase().indexOf("chrome") > -1);
};

const isFirefox = () => {
    return (navigator.userAgent.toLowerCase().indexOf('firefox') > -1);
};

const isSafari = () => {
    return (!!navigator.userAgent.toLowerCase().match(/version\/[\d.]+.*safari/) && !window.MSStream);
};

const isEdge = () => {
    return (navigator.userAgent.toLowerCase().indexOf("edge/") > -1 || window.navigator.userAgent.toLowerCase().indexOf("edg/") > -1);
};

const isOpera = () => {
    return (navigator.userAgent.toLowerCase().match(/opera|opr\//));
};

/**
 * Clear cache and reload the page
 */
const clearCacheAndReload = () => {
    if ('caches' in window) {
        caches.keys().then(names => {
            names.forEach(name => {
                caches.delete(name);
            });
        });
    }

    setTimeout(() => {
        refreshing.value = true;
        window.location.reload(true);
    }, 100);
};

/**
 * Display or hide download app UI elements
 */
const displayHideDownloadApp = (display = true) => {
    setTimeout(() => {
        const elements = document.querySelectorAll('.download-app-container');
        if (elements.length > 0) {
            elements.forEach((element) => {
                if (display) {
                    element.classList.remove('d-none');
                } else {
                    element.classList.add('d-none');
                }
            });
        }
    }, 1000);
};

/**
 * Composable for PWA management using Composition API
 * @param {Object} installEvent - PWA install prompt event (optional)
 * @param {Function} dismissPWAPrompt - Function to dismiss PWA prompt (optional)
 * @returns {Object} PWA management methods and state
 */
export function usePwa(installEvent = null, dismissPWAPrompt = null) {
    let swUpdateHandler = null;
    let controllerChangeHandler = null;

    /**
     * Handle service worker update event
     */
    const updateAvailable = (event) => {
        const isRefreshing = localStorage.getItem('sw-refreshing');
        if (isRefreshing === 'true') {
            return;
        }
        
        registration.value = event.detail;
        updateExists.value = true;
    };

    /**
     * Refresh the app with the new service worker
     */
    const refreshApp = () => {
        if (!registration.value || !registration.value.waiting) {
            console.log('No service worker registration available');
            return;
        }
        
        localStorage.setItem('sw-refreshing', 'true');
        registration.value.waiting.postMessage({ type: 'SKIP_WAITING' });
        updateExists.value = false;

        clearCacheAndReload();
    };

    /**
     * Install PWA
     */
    const installPwa = () => {
        const installEventValue = installEvent?.value || installEvent;
        if (!installEventValue) {
            console.log('No install event available');
            return;
        }

        installEventValue.prompt();
        installEventValue.userChoice.then((choice) => {
            if (dismissPWAPrompt) {
                dismissPWAPrompt();
            }
            if (choice.outcome === "accepted") {
                // PWA accepted
            } else {
                // PWA declined
            }
        });
    };

    /**
     * Handle download app button click
     */
    const handleDownloadAppClick = (event) => {
        const link = event.target.closest('.app-link');
        if (link) {
            const installEventValue = installEvent?.value || installEvent;
            if (installEventValue) {
                event.preventDefault();
                downloadApp();
            }
            // On iOS/Firefox/Safari, beforeinstallprompt never fires so installEvent
            // is null — fall through to the link's default href navigation.
        }
    };

    /**
     * Download/Install the app
     */
    const downloadApp = () => {
        const installEventValue = installEvent?.value || installEvent;
        if (!installEventValue) {
            console.log('no installEvent');
            return;
        }
        installEventValue.prompt();
        installEventValue.userChoice.then((choice) => {
            if (choice.outcome === "accepted") {
                displayHideDownloadApp(false);
            }
        });
    };

    /**
     * Setup service worker event listeners
     */
    const setupServiceWorkerListeners = () => {
        swUpdateHandler = updateAvailable;
        document.addEventListener('swUpdated', swUpdateHandler, { once: true });

        controllerChangeHandler = () => {
            if (refreshing.value) return;
            refreshing.value = true;
            localStorage.removeItem('sw-refreshing');
            
            clearCacheAndReload();
        };
        navigator.serviceWorker.addEventListener('controllerchange', controllerChangeHandler);
    };

    /**
     * Cleanup service worker event listeners
     */
    const cleanupServiceWorkerListeners = () => {
        if (swUpdateHandler) {
            document.removeEventListener('swUpdated', swUpdateHandler);
            swUpdateHandler = null;
        }
        if (controllerChangeHandler) {
            navigator.serviceWorker.removeEventListener('controllerchange', controllerChangeHandler);
            controllerChangeHandler = null;
        }
    };

    // Auto-setup on mount and cleanup on unmount
    onMounted(() => {
        setupServiceWorkerListeners();
        const isRefreshing = localStorage.getItem('sw-refreshing');
        if (isRefreshing === 'true') {
            setTimeout(() => {
                localStorage.removeItem('sw-refreshing');
            }, 5000);
        }
    });

    onUnmounted(() => {
        cleanupServiceWorkerListeners();
    });

    return {
        // State (readonly)
        refreshing: readonly(refreshing),
        registration: readonly(registration),
        updateExists: readonly(updateExists),
        // Browser detection
        isIOS,
        isInStandaloneMode,
        isChrome,
        isFirefox,
        isSafari,
        isEdge,
        isOpera,
        // PWA methods
        installPwa,
        downloadApp,
        handleDownloadAppClick,
        displayHideDownloadApp,
        // Service worker methods
        updateAvailable,
        refreshApp,
        clearCacheAndReload,
        setupServiceWorkerListeners,
        cleanupServiceWorkerListeners
    };
}

// Legacy mixin export for backward compatibility
export default {
    data() {
        return {
            refreshing: false,
            registration: null,
            updateExists: false,
        };
    },
    created() {
        this._swUpdateHandler = (event) => {
            const isRefreshing = localStorage.getItem('sw-refreshing');
            if (isRefreshing === 'true') {
                return;
            }
            
            this.registration = event.detail;
            this.updateExists = true;
        };
        document.addEventListener('swUpdated', this._swUpdateHandler, { once: true });

        this._controllerChangeHandler = () => {
            if (this.refreshing) return;
            this.refreshing = true;
            localStorage.removeItem('sw-refreshing');
            
            this.clearCacheAndReload();
        };
        navigator.serviceWorker.addEventListener('controllerchange', this._controllerChangeHandler);
        
        const isRefreshing = localStorage.getItem('sw-refreshing');
        if (isRefreshing === 'true') {
            setTimeout(() => {
                localStorage.removeItem('sw-refreshing');
            }, 5000);
        }
    },
    beforeDestroy() {
        if (this._swUpdateHandler) {
            document.removeEventListener('swUpdated', this._swUpdateHandler);
        }
        if (this._controllerChangeHandler) {
            navigator.serviceWorker.removeEventListener('controllerchange', this._controllerChangeHandler);
        }
    },
    methods: {
        installPwa() {
            this.installEvent.prompt();
            this.installEvent.userChoice.then((choice) => {
                this.dismissPWAPrompt();
                if (choice.outcome === "accepted") {
                    // this.pwaInstalled();
                } else {
                    // Do something additional if the user declined
                }
            });
        },
        isIOS() {
            return isIOS();
        },
        isInStandaloneMode() {
            return isInStandaloneMode();
        },
        isChrome() {
            return isChrome();
        },
        isFirefox() {
            return isFirefox();
        },
        isSafari() {
            return isSafari();
        },
        isEdge() {
            return isEdge();
        },
        isOpera() {
            return isOpera();
        },
        updateAvailable(event) {
            this.registration = event.detail;
            this.updateExists = true;
        },
        refreshApp() {
            if (!this.registration || !this.registration.waiting) {
                console.log('No service worker registration available');
                return;
            }
            
            localStorage.setItem('sw-refreshing', 'true');
            
            this.registration.waiting.postMessage({ type: 'SKIP_WAITING' });
            this.updateExists = false;

            this.clearCacheAndReload();
        },
        clearCacheAndReload() {
            clearCacheAndReload();
        },
        displayHideDownloadApp(display = true) {
            displayHideDownloadApp(display);
        },
        handleDownloadAppClick(event) {
            const link = event.target.closest('.app-link');
            if (link) {
                if (this.installEvent) {
                    event.preventDefault();
                    this.downloadApp();
                }
                // On iOS/Firefox/Safari, beforeinstallprompt never fires so installEvent
                // is null — fall through to the link's default href navigation.
            }
        },
        downloadApp() {
            if (!this.installEvent) {
                console.log('no installEvent');
                return;
            }
            this.installEvent.prompt();
            this.installEvent.userChoice.then((choice) => {
                if (choice.outcome === "accepted") {
                    this.displayHideDownloadApp(false);
                }
            });
        },
    }
};

